import React, { useMemo, useState } from 'react';
import Select from '../../components/select';
import { useExtendState } from 'helpers/use_extend_state';
import Button from '../../components/button';
import MultipleSelect from '../../components/multiple_select';
import Switch from '../../components/switch';
import { MembershipPlan } from '../../types/membership_plan';
import useGym from 'helpers/use_gym';

type RefundFilter = 'include' | 'exclude' | 'refunds_only';

interface InvoicesFiltersModalProps {
  modalInstance: any;
  filters: any;
  paymentStatuses: string[];
  contactPage: boolean;
  displayStatus: (status: string) => string;
  membershipPlans: MembershipPlan[];
  locations: any[];
  membershipPlanCategories: any[];
  sources: any[];
  paymentMethodFilterAvailable: boolean;
  paymentMethods: any[];
  refundsOptions: any[];
  getRefundsFilters: (val: RefundFilter) => any;
  getInitialRefunds: (filters: any) => RefundFilter;
}

const InvoicesFiltersModal = ({
  modalInstance,
  filters: externalFilters,
  contactPage,
  paymentStatuses,
  displayStatus,
  membershipPlans,
  locations,
  membershipPlanCategories,
  sources,
  paymentMethodFilterAvailable,
  paymentMethods,
  refundsOptions,
  getRefundsFilters,
  getInitialRefunds,
}: InvoicesFiltersModalProps) => {
  const gym = useGym();
  const [filters, extendFilters] = useExtendState<any>(externalFilters);
  const [refunds, setRefunds] = useState<RefundFilter>(getInitialRefunds(filters));

  const cancel = () => {
    modalInstance.dismiss('cancel');
  };

  const submit = () => {
    modalInstance.close(filters);
  };

  const isRefundsAvailable = () => {
    return gym.refunds_enabled && gym.uses_ezidebit;
  };

  const selectedStatuses = useMemo(() => {
    return paymentStatuses.filter((status) => filters[status]);
  }, [filters, paymentStatuses]);

  return (
    <div>
      <div className="modal-header">
        <a aria-hidden="true" className="close-modal" onClick={cancel} title="Close">
          <i className="icon-cross"></i>
        </a>
        <h4 className="modal-title">Filters</h4>
      </div>
      <div className="modal-body modal-body-visible">
        {contactPage ? (
          <>
            {paymentStatuses.map((status) => (
              <div className="form-group" key={status} style={{ minWidth: 80 }}>
                <label className="control-label">{displayStatus(status)}</label>
                <div>
                  <Switch
                    checked={filters[status]}
                    onChange={(val) => {
                      extendFilters({ [status]: val });
                    }}
                  />
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="form-group">
              <label className="control-label">Status</label>
              <div>
                <MultipleSelect
                  fullWidth
                  value={selectedStatuses}
                  options={paymentStatuses.map((status) => ({
                    value: status,
                    label: displayStatus(status),
                  }))}
                  onChange={(statuses) => {
                    const updatedFilters = { ...filters };
                    paymentStatuses.forEach((status) => {
                      updatedFilters[status] = !statuses.length || statuses.indexOf(status) >= 0;
                    });
                    extendFilters(updatedFilters);
                  }}
                  alignLeft
                  emptySelectsAll
                />
              </div>
            </div>

            <div className="form-group">
              <label className="control-label">Membership</label>
              <div>
                <MultipleSelect
                  fullWidth
                  value={filters.membership_plan_ids}
                  options={membershipPlans}
                  onChange={(membership_plan_ids) => extendFilters({ membership_plan_ids })}
                  emptySelectsAll
                />
              </div>
            </div>

            <div className="form-group">
              <label className="control-label">Location</label>
              <div>
                <MultipleSelect
                  fullWidth
                  value={filters.location_ids}
                  options={locations}
                  onChange={(location_ids) => extendFilters({ location_ids })}
                  emptySelectsAll
                />
              </div>
            </div>

            <div className="form-group">
              <label className="control-label">Category</label>
              <div>
                <MultipleSelect
                  fullWidth
                  value={filters.category_ids}
                  options={membershipPlanCategories}
                  onChange={(category_ids) => extendFilters({ category_ids })}
                  emptySelectsAll
                />
              </div>
            </div>

            {paymentMethodFilterAvailable && (
              <div className="form-group">
                <label className="control-label"> Payment Method</label>
                <div>
                  <Select
                    value={filters.payment_method}
                    options={paymentMethods}
                    onChange={(payment_method) => {
                      extendFilters({ payment_method });
                    }}
                    emptyLabel="Show all"
                    allowEmptyOption
                    variant="v2"
                    size="sm"
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="control-label">Added From</label>
              <div>
                <MultipleSelect
                  fullWidth
                  value={filters.sources}
                  options={sources.map((item) => ({ value: item[1], label: item[0] }))}
                  onChange={(sources) => extendFilters({ sources })}
                  emptySelectsAll
                />
              </div>
            </div>

            {isRefundsAvailable() && (
              <div className="form-group">
                <label className="control-label">Refunds</label>
                <Select
                  value={refunds}
                  options={refundsOptions}
                  onChange={(refunds: RefundFilter) => {
                    const refundsFilters = getRefundsFilters(refunds);
                    setRefunds(refunds);
                    extendFilters(refundsFilters);
                  }}
                  variant="v2"
                  size="sm"
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="modal-footer">
        <Button variant="hollow" onClick={cancel}>
          Cancel
        </Button>

        <Button variant="primary--v2" onClick={submit}>
          Done
        </Button>
      </div>
    </div>
  );
};

angular.module('gym').value('InvoicesFiltersModal', InvoicesFiltersModal);
